import type { CalendarService } from "@common/services/calendar-event-service";
import type {
  CalendarConnection,
  CalendarEvent,
  CalendarEventId,
  EventUpdateDelta,
} from "@common/types/calendar-event";
import type { GroupKey, Todo, TodoId } from "@common/types/todo";
import type { DisplayMode } from "@common/utils/todo-utils";
import {
  eventsByGroup,
  type EventListByGroup,
} from "@common/utils/calendar-event-group-utils";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import type SharedTodoStore from "@common/stores/shared-todo-store";
import type UserPreferencesStore from "@common/stores/user-preferences-store";
import type UserStore from "@common/stores/user-store";
import type { SpaceId } from "@common/types/space";
import { CommonEvents } from "@common/services/common-events";
import { generateId } from "@common/utils/id";

export interface CalendarStoreSpaceProvider {
  currentSpaceId: SpaceId;
  spaces: { id: SpaceId; name: string }[];
}

const SYNC_STALE_MS = 5 * 60 * 1000; // 5 minutes

export type TodoToEventScheduleFields = {
  isAllDay: boolean;
  startTime: number;
  endTime: number;
  startDate: string | null;
  endDate: string | null;
};

export default class CalendarStore {
  @observable
  eventIdMap: { [id: CalendarEventId]: CalendarEvent } = {};
  @observable
  connections: CalendarConnection[] = [];
  @observable
  isLoading = false;
  @observable
  isSyncing = false;
  @observable
  lastFetchedAt: number | null = null;
  @observable
  lastSyncedAt: number | null = null;

  private _calendarService: CalendarService;
  private _userPreferencesStore: UserPreferencesStore;
  private _userStore: UserStore;
  private _spaceProvider: CalendarStoreSpaceProvider;

  constructor({
    eventService,
    userPreferencesStore,
    userStore,
    spaceProvider,
  }: {
    eventService: CalendarService;
    userPreferencesStore: UserPreferencesStore;
    userStore: UserStore;
    spaceProvider: CalendarStoreSpaceProvider;
  }) {
    this._calendarService = eventService;
    this._userPreferencesStore = userPreferencesStore;
    this._userStore = userStore;
    this._spaceProvider = spaceProvider;
    makeObservable(this);
  }

  @computed
  get events(): CalendarEvent[] {
    const hiddenConnectionIds = new Set(
      this.connections.filter((c) => !c.showEvents).map((c) => c.id),
    );

    const spaceVis =
      this._userPreferencesStore.userPreferences?.spaceCalendarVisibility;
    const currentSpaceId = this._spaceProvider.currentSpaceId;
    const spaceAllowedExternalIds = spaceVis?.[currentSpaceId];
    const allowedConnectionIds = spaceAllowedExternalIds
      ? new Set(
          this.connections
            .filter((c) =>
              spaceAllowedExternalIds.includes(c.externalCalendarId),
            )
            .map((c) => c.id),
        )
      : undefined;

    return Object.values(this.eventIdMap).filter((e) => {
      if (e.deleted) return false;
      if (hiddenConnectionIds.has(e.connectionId)) return false;
      if (allowedConnectionIds && !allowedConnectionIds.has(e.connectionId))
        return false;
      return true;
    });
  }

  @computed
  get defaultCalendarConnection(): CalendarConnection | undefined {
    const id =
      this._userPreferencesStore.userPreferences?.defaultCalendarConnectionId;
    if (id) {
      const match = this.connections.find((c) => c.id === id);
      if (match && match.provider !== "ical") return match;
    }
    return (
      this.connections.find((c) => c.provider !== "ical") ?? this.connections[0]
    );
  }

  /**
   * Returns the set of externalCalendarIds visible in the current space.
   * `undefined` means all connections are visible (no restriction configured).
   */
  @computed
  get currentSpaceVisibleExternalCalendarIds(): string[] | undefined {
    const spaceVis =
      this._userPreferencesStore.userPreferences?.spaceCalendarVisibility;
    return spaceVis?.[this._spaceProvider.currentSpaceId];
  }

  /** Whether a given connection is visible in the current space. */
  isConnectionVisibleInCurrentSpace(externalCalendarId: string): boolean {
    return this.isConnectionVisibleInSpace(
      externalCalendarId,
      this._spaceProvider.currentSpaceId,
    );
  }

  /** Whether a given connection is visible in a specific space. */
  isConnectionVisibleInSpace(
    externalCalendarId: string,
    spaceId: SpaceId,
  ): boolean {
    const spaceVis =
      this._userPreferencesStore.userPreferences?.spaceCalendarVisibility;
    const allowed = spaceVis?.[spaceId];
    return !allowed || allowed.includes(externalCalendarId);
  }

  /** Persist per-space calendar visibility for a given space. */
  async setSpaceVisibleConnections(
    spaceId: SpaceId,
    externalCalendarIds: string[],
  ) {
    const userId = this._userStore.userId;
    if (!userId) return;

    const existing =
      this._userPreferencesStore.userPreferences?.spaceCalendarVisibility ?? {};

    const allExternalIds = this.connections
      .filter((c) => c.showEvents)
      .map((c) => c.externalCalendarId);
    const isAllVisible =
      externalCalendarIds.length === allExternalIds.length &&
      allExternalIds.every((id) => externalCalendarIds.includes(id));

    const updated = { ...existing };
    if (isAllVisible) {
      delete updated[spaceId];
    } else {
      updated[spaceId] = externalCalendarIds;
    }

    const hasEntries = Object.keys(updated).length > 0;
    await this._userPreferencesStore.update(userId, {
      spaceCalendarVisibility: hasEntries ? updated : null,
    });
  }

  /** Toggle a single connection's visibility in a specific space. */
  async toggleConnectionVisibilityForSpace(
    externalCalendarId: string,
    spaceId: SpaceId,
    visible: boolean,
  ) {
    const spaceVis =
      this._userPreferencesStore.userPreferences?.spaceCalendarVisibility;
    const allowed = spaceVis?.[spaceId];
    let next: string[];

    if (!allowed) {
      next = this.connections
        .filter((c) => c.showEvents)
        .map((c) => c.externalCalendarId)
        .filter((id) => (id === externalCalendarId ? visible : true));
    } else if (visible) {
      next = allowed.includes(externalCalendarId)
        ? [...allowed]
        : [...allowed, externalCalendarId];
    } else {
      next = allowed.filter((id) => id !== externalCalendarId);
    }

    await this.setSpaceVisibleConnections(spaceId, next);
  }

  getEventsByGroup(displayMode: DisplayMode): EventListByGroup {
    return eventsByGroup(this.events, displayMode);
  }

  getEventsForGroupKey(
    groupKey: GroupKey,
    displayMode: DisplayMode,
  ): CalendarEvent[] {
    const groups = this.getEventsByGroup(displayMode);
    return groups[groupKey] ?? [];
  }

  @action
  setEvents(events: CalendarEvent[]) {
    const map: { [id: CalendarEventId]: CalendarEvent } = {};
    for (const e of events) {
      map[e.id] = e;
    }
    this.eventIdMap = map;
  }

  @action
  setConnections(connections: CalendarConnection[]) {
    this.connections = connections;
  }

  @action
  async fetchEvents(range?: { from: number; to: number }) {
    const needsSync =
      !this.lastSyncedAt || Date.now() - this.lastSyncedAt > SYNC_STALE_MS;

    if (needsSync) {
      this.syncCalendars();
    }

    this.isLoading = true;
    try {
      const events = range
        ? await this._calendarService.getEventsInRange(range.from, range.to)
        : await this._calendarService.getEvents();
      runInAction(() => {
        this.setEvents(events);
        this.lastFetchedAt = Date.now();
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action
  async syncCalendars() {
    if (this.isSyncing) return;
    this.isSyncing = true;
    try {
      await this._calendarService.syncCalendars();
      runInAction(() => {
        this.lastSyncedAt = Date.now();
      });
      const events = await this._calendarService.getEvents();
      runInAction(() => {
        this.setEvents(events);
        this.lastFetchedAt = Date.now();
      });
    } catch (err) {
      console.error("Calendar sync failed:", err);
    } finally {
      runInAction(() => {
        this.isSyncing = false;
      });
    }
  }

  @action
  async fetchConnections() {
    const connections = await this._calendarService.getCalendarConnections();
    runInAction(() => {
      this.setConnections(connections);
    });
  }

  @action
  createEvent(event: CalendarEvent, options?: { immediate: boolean }) {
    this.eventIdMap[event.id] = event;
    this._calendarService.createEvent(event, options);
  }

  @action
  updateEvent(delta: EventUpdateDelta, options?: { immediate: boolean }) {
    const existing = this.eventIdMap[delta.id];
    if (existing) {
      this.eventIdMap[delta.id] = { ...existing, ...delta };
    }
    this._calendarService.updateEvent(delta, options);
  }

  @action
  deleteEvent(eventId: CalendarEventId, options?: { immediate: boolean }) {
    const existing = this.eventIdMap[eventId];
    if (existing) {
      this.eventIdMap[eventId] = { ...existing, deleted: true };
    }
    this._calendarService.deleteEvent(eventId, options);
  }

  @action
  async setConnectionShowEvents(connectionId: string, showEvents: boolean) {
    // Optimistically update the connection list
    runInAction(() => {
      this.connections = this.connections.map((c) =>
        c.id === connectionId ? { ...c, showEvents } : c,
      );
    });

    await this._calendarService.updateCalendarConnectionShowEvents(
      connectionId,
      showEvents,
    );
  }

  @action
  async disconnectCalendar(connectionId: string) {
    await this._calendarService.disconnectCalendar(connectionId);

    const userId = this._userStore.userId;
    if (
      userId &&
      this._userPreferencesStore.userPreferences
        ?.defaultCalendarConnectionId === connectionId
    ) {
      await this._userPreferencesStore.update(userId, {
        defaultCalendarConnectionId: null,
      });
    }

    runInAction(() => {
      this.connections = this.connections.filter((c) => c.id !== connectionId);
      // Remove events from this connection
      const newMap: { [id: CalendarEventId]: CalendarEvent } = {};
      for (const [id, event] of Object.entries(this.eventIdMap)) {
        if (event.connectionId !== connectionId) {
          newMap[id as CalendarEventId] = event;
        }
      }
      this.eventIdMap = newMap;
    });
  }

  private finalizeTodoAsNewEvent(
    todo: Todo,
    todoStore: SharedTodoStore,
    conn: CalendarConnection,
    title: string,
    schedule: TodoToEventScheduleFields,
  ) {
    const now = Date.now();
    const event: CalendarEvent = {
      id: generateId(),
      userId: todo.owner,
      connectionId: conn.id,
      externalId: null,
      icalUid: null,
      etag: null,
      recurringEventId: null,
      originalStartTime: null,
      title,
      description: null,
      location: null,
      url: null,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      startDate: schedule.startDate,
      endDate: schedule.endDate,
      isAllDay: schedule.isAllDay,
      timezone: null,
      status: "confirmed",
      transparency: "busy",
      organizerEmail: null,
      attendees: null,
      source: "local",
      syncStatus: conn.provider === "google" ? "pending_create" : "synced",
      localVersion: 0,
      deleted: false,
      createdDate: now,
      lastEditDate: now,
    };

    this.createEvent(event, { immediate: true });
    todoStore.deleteTodo(todo.id);
  }

  /**
   * Convert a todo into a calendar event using an explicit schedule (e.g. from /event NL parsing).
   */
  @action
  convertTodoToEventWithSchedule(
    todoId: TodoId,
    todoStore: SharedTodoStore,
    schedule: TodoToEventScheduleFields,
    eventTitle: string,
  ) {
    const conn = this.defaultCalendarConnection;
    if (!conn || conn.provider === "ical") {
      CommonEvents.emit("PUBLISH_TOAST", {
        title: "No Writable Calendar",
        content: conn
          ? "iCal feeds are read-only. Connect a Google Calendar to create events."
          : "Connect at least one calendar to enable creation of events.",
        titleColor: "warning",
        duration: 3000,
      });
      return;
    }

    const todo = todoStore.todoIdMap[todoId];
    if (!todo)
      throw new Error(`Could not convert to event, todo not found: ${todoId}`);

    if (!schedule.isAllDay && schedule.endTime <= schedule.startTime) {
      CommonEvents.emit("PUBLISH_TOAST", {
        title: "Invalid time range",
        content: "End time must be after start time.",
        titleColor: "warning",
        duration: 3000,
      });
      return;
    }

    const title = eventTitle.trim() || "(no title)";
    this.finalizeTodoAsNewEvent(todo, todoStore, conn, title, schedule);
  }
}
